import { Close, Timeline } from '@mui/icons-material';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { formatDate } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';

interface BlacklistHistoryItem {
  id: number;
  businessIdentifier: string;
  businessName: string;
  action: 'agregó' | 'eliminó';
  actionDate: string;
  reason?: string;
  userId: number;
  userName: string;
}

interface HistoryDialogProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  fetchBlacklistHistory: (params: {
    page: number;
    pageSize: number;
  }) => Promise<any>;
}

const HistoryDialog: React.FC<HistoryDialogProps> = ({
  open,
  onClose,
  businessId,
  businessName,
  fetchBlacklistHistory,
}) => {
  const [historyData, setHistoryData] = useState<BlacklistHistoryItem[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);

  const tableHeaders = [
    {
      label: 'ID',
      key: 'id',
      type: 'numeric-id',
    },
    {
      label: 'Acción',
      key: 'action',
      type: 'blacklist-action',
    },
    {
      label: 'Razón',
      key: 'reason',
    },
    {
      label: 'Usuario',
      key: 'userName',
    },
    {
      label: 'Fecha',
      key: 'actionDate',
      type: 'date',
    },
  ];

  const searchData = async (params: { page: number; pageSize: number }) => {
    try {
      setLoading(true);
      const response = await fetchBlacklistHistory(params);
      setHistoryData(response.data);
      setPagination(response.pagination);
      return response;
    } catch (error) {
      console.error('Error fetching history:', error);
      return { data: [], pagination: pagination };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      searchData({ page: 1, pageSize: 10 });
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              p: 0.75,
              borderRadius: '50%',
            }}
          >
            <Timeline sx={{ color: 'primary.main' }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Historial de Blacklist
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                {businessName}
              </Typography>
              <Chip
                label={`${pagination.total} registros`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <TableWithPagination
          data={historyData}
          setData={setHistoryData}
          dataHeaders={tableHeaders}
          pagination={pagination}
          setPagination={setPagination}
          searchData={searchData}
          loading={loading}
          setLoading={setLoading}
          exportToFile="xlsx"
          exportComplete
          lastElement
        />
      </DialogContent>
    </Dialog>
  );
};

export default HistoryDialog;
